import { useGridApiRef } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { fetchLatestBidEventsByStationAndDate } from "../../api/bid";
import { getBidTypeFeaturesByStation } from "../../api/commonConfig/bidConfig";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import PageLoading from "../../components/PageLoading";
import UnauthorizedMessage from "../../components/UnauthorizedMessage";
import BidAdminFilterForm from "../../components/bid/BidAdminFilterForm";
import BidHistoryDialog from "../../components/bid/BidHistoryDialog.jsx";
import BidInfoBanner from "../../components/bid/BidInfoBanner";
import BidTable from "../../components/bid/BidTable";
import CreateBidDialog from "../../components/bid/CreateBidDialog";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import {
  selectAdminBidData,
  updateBidTypeFilters,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
} from "../../slices/adminBid/adminBidSlice";
import { selectWorkgroupData } from "../../slices/commonConfig/commonConfigSlice";
import { selectStationFilter, selectUserSession, selectWorkgroupFilters, updateStationFilter, updateWorkgroupFilters } from "../../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../../util/authentication";
import { BID_STATUS } from "../../util/bid/bidConstants";
import { validateBidEndDateFilter, validateBidStartDateFilter } from "../../util/bid/bidInputValidation";
import { getScrubbedBidEvents } from "../../util/bid/bidScrubber";
import {
  DEFAULT_ADMIN_BID_END_DATE_FILTER,
  DEFAULT_ADMIN_BID_START_DATE_FILTER,
  filterBids,
  filterBidsOnInfoFilter,
} from "../../util/bid/filterBids";
import { initializeSearchIndex, searchBids } from "../../util/bid/searchBids";
import { getAdminStation } from "../../util/commonUtils";
import { BREADCRUMBS_TITLE, ERROR_MAX_PAYLOAD_LIMIT_MSG, FEATURES, HOME_PATH } from "../../util/constants";
import {
  deserializeDateFromRedux,
  getISODateTimeString,
  serializeDateToRedux,
  set_time_to_end_of_day,
} from "../../util/dateTimeFormatter";
import { bidDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import { ADMIN_MOBILE_WARNING_MESSAGE } from "../../util/messageConstants";
import { mergeWebSocketAdminBids } from "../../util/webSocketUtils";

const BidAdministrationPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const bidData = useSelector(selectAdminBidData);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();
  const history = useHistory();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [stationBids, setStationBids] = useState([]);
  const [filteredStationBids, setFilteredStationBids] = useState([]);
  const [searchedStationBids, setSearchedStationBids] = useState([]);
  const [isSearch, setIsSearch] = useState(false);
  const [isCreateBidDialogOpen, setIsCreateBidDialogOpen] = useState(false);
  const [bidInfoFilter, setBidInfoFilter] = useState("");
  const [bidInfoFilteredStationBids, setBidInfoFilteredStationBids] = useState([]);
  const [selectedBidForAuditHistory, setSelectedBidForAuditHistory] = useState("");
  const [bidTypes, setBidTypes] = useState([]);

  // We need these Refs because changes to them are synchronous (as opposed to the state variables being async) and
  // knowing that, we can check the value of them upon getting a response back to ensure queries from changing the
  // table filters are not overwritten in the event a filter is changed in the middle of waiting for a response
  // See doesResponseQueryParamsMatchCurrentFilters and how it is used for more details.
  const stationFilterRef = useRef("");
  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const bidWebSocketDataRef = useRef({});
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const bidTableDesktopRef = useGridApiRef();
  const isAdminPageRef = useRef(true);
  const isApiSubscribedRef = useRef(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (props.isDesktopView) {
        if (bidData.startDateFilter === null) {
          initializeFilterState();
        }
        stationFilterRef.current = stationFilter;
        if (bidData?.startDateFilterErrors === "" && bidData?.endDateFilterErrors === "") {
          updateStationBidSnapshotList(
            userSession.eid,
            stationFilter,
            bidData.dateTypeFilter,
            getStartDateFilterSafely(),
            getEndDateFilterSafely()
          );
        } else {
          setIsLoading(false);
        }
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState, props.isDesktopView]);

  useEffect(() => {
    bidWebSocketDataRef.current = webSocketEventData.bidAdminEvents;
    if (Object.values(webSocketEventData.bidAdminEvents).length > 0) {
      if (!isFirstLoad) {
        setStationBids((prevState) => {
          return mergeWebSocketAdminBids(webSocketEventData.bidAdminEvents, prevState);
        });
        bidWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.bidAdminEvents]);

  useEffect(() => {
    let newFilteredBids = filterBids(
      stationBids,
      bidData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      bidData.bidTypeFilters,
      bidData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    applyFilters(newFilteredBids);
    if (bidData.searchString !== "") {
      handleSearchSubmit(bidData.searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationBids]);

  useEffect(() => {
    getBidTypeFeaturesByStation(stationFilter)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error(result.message);
        } else if (result.bid_types) {
          setBidTypes(result.bid_types);
          if (isFirstLoad && bidData.bidTypeFilters.length === 0) dispatch(updateBidTypeFilters(result.bid_types));
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  // We need this initialization since these fields are dependent on the stationFilter value
  const initializeFilterState = () => {
    dispatch(updateStartDateFilter(serializeDateToRedux(DEFAULT_ADMIN_BID_START_DATE_FILTER(stationFilter))));
    dispatch(updateEndDateFilter(serializeDateToRedux(DEFAULT_ADMIN_BID_END_DATE_FILTER(stationFilter))));
  };

  // These safe functions are used to be sure the default value is provided in the event the redux state is not available yet
  const getStartDateFilterSafely = () => {
    return bidData.startDateFilter
      ? deserializeDateFromRedux(bidData.startDateFilter)
      : DEFAULT_ADMIN_BID_START_DATE_FILTER(stationFilter);
  };

  const getEndDateFilterSafely = () => {
    return bidData.endDateFilter
      ? deserializeDateFromRedux(bidData.endDateFilter)
      : DEFAULT_ADMIN_BID_END_DATE_FILTER(stationFilter);
  };

  const updateStationBidSnapshotList = (eid, station, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setIsLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchLatestBidEventsByStationAndDate(eid, station, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(bidDataErrorCodeToErrorMessageMap.bidDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            let scrubbedBidEvents = getScrubbedBidEvents(result.events);
            setStationBids(mergeWebSocketAdminBids(bidWebSocketDataRef.current, scrubbedBidEvents));
            bidWebSocketDataRef.current = {};
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsError(true);
        setErrorObject(bidDataErrorCodeToErrorMessageMap.unexpectedBidDataError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      (responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.startDate === getISODateTimeString(startDateFilterRef.current)) &&
      (responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.endDate === getISODateTimeString(endDateFilterRef.current))
    );
  };

  const applySearch = (searchString, searchedBids) => {
    if (searchString === "") {
      setIsSearch(false);
    } else {
      setIsSearch(true);
    }
    setSearchedStationBids(searchedBids);
    applyBidTableScroll();
    setLoading(false);
  };

  const applyFilters = (filteredBids, infoFilter) => {
    if (infoFilter) {
      const infoFilteredBids = filterBidsOnInfoFilter(filteredBids, infoFilter);
      setBidInfoFilteredStationBids(infoFilteredBids);
      initializeSearchIndex(infoFilteredBids, isAdminPageRef.current);
    } else {
      initializeSearchIndex(filteredBids, isAdminPageRef.current);
      setBidInfoFilter("");
      setBidInfoFilteredStationBids([]);
    }

    if (isSearch) {
      searchBids(bidData.searchString, applySearch, isAdminPageRef.current);
    }

    setFilteredStationBids(filteredBids);
  };

  const applyBidTableScroll = () => {
    // This ref scrolls desktop trades to top
    const currentDesktop = bidTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const handleDateTypeFilterChange = (event) => {
    updateStationBidSnapshotList(
      userSession.eid,
      stationFilter,
      event.target.value,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter)
    );
    dispatch(updateDateTypeFilter(event.target.value));
    applyBidTableScroll();
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateBidStartDateFilter(
      date,
      deserializeDateFromRedux(bidData.endDateFilter),
      stationFilter
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      updateStationBidSnapshotList(
        userSession.eid,
        stationFilter,
        bidData.dateTypeFilter,
        date,
        deserializeDateFromRedux(bidData.endDateFilter)
      );
      dispatch(updateEndDateFilterErrors(""));
      applyBidTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateBidEndDateFilter(
      date,
      deserializeDateFromRedux(bidData.startDateFilter),
      stationFilter
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      updateStationBidSnapshotList(
        userSession.eid,
        stationFilter,
        bidData.dateTypeFilter,
        deserializeDateFromRedux(bidData.startDateFilter),
        date
      );
      dispatch(updateStartDateFilterErrors(""));
      applyBidTableScroll();
    }
  };

  const handleBidTypeFilterChange = (event) => {
    let newBidTypeFilterSelectedList = event.target.value;
    if (newBidTypeFilterSelectedList.includes("Select All")) {
      if (isBidTypeSelectAllChecked()) {
        newBidTypeFilterSelectedList = [];
      } else {
        newBidTypeFilterSelectedList = Object.values(bidTypes);
      }
    }
    setLoading(true);
    let newFilteredBids = filterBids(
      stationBids,
      bidData.dateTypeFilter,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter),
      newBidTypeFilterSelectedList,
      bidData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateBidTypeFilters(newBidTypeFilterSelectedList));
    applyFilters(newFilteredBids);
    setLoading(false);
    applyBidTableScroll();
  };

  const isBidTypeSelectAllChecked = () => {
    return bidData.bidTypeFilters.length === bidTypes.length;
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = Object.values(BID_STATUS);
      }
    }

    setLoading(true);
    let newFilteredBids = filterBids(
      stationBids,
      bidData.dateTypeFilter,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter),
      bidData.bidTypeFilters,
      newStatusFilterSelectedList,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredBids);
    setLoading(false);
    applyBidTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return bidData.statusFilters.length === Object.values(BID_STATUS).length;
  };

  const handleWorkgroupFilterChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = Object.keys(workgroupData);
      }
    }
    setLoading(true);
    let newFilteredBids = filterBids(
      stationBids,
      bidData.dateTypeFilter,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter),
      bidData.bidTypeFilters,
      bidData.statusFilters,
      newWorkgroupFilterSelectedList,
      isAdminPageRef.current
    );
    dispatch(updateWorkgroupFilters(newWorkgroupFilterSelectedList));
    applyFilters(newFilteredBids);
    setLoading(false);
    applyBidTableScroll();
  };

  const handleBidInfoFilterChange = (infoFilter) => {
    if (infoFilter === bidInfoFilter) {
      // deselect the filter if it is clicked twice
      infoFilter = "";
    }
    setBidInfoFilter(infoFilter);
    setLoading(true);
    applyFilters(filteredStationBids, infoFilter);
    setLoading(false);
    applyBidTableScroll();
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return (
      workgroupFilters.length !== Object.keys(workgroupData).length && workgroupFilters.length !== 0
    );
  };

  const isWorkgroupSelectAllChecked = () => {
    return workgroupFilters.length === Object.keys(workgroupData).length;
  };

  const handleStationFilterChange = (event) => {
    dispatch(updateStationFilter(event.target.value));
    bidWebSocketDataRef.current = {};
    stationFilterRef.current = event.target.value;
    setIsLoading(true);
    updateStationBidSnapshotList(
      userSession.eid,
      event.target.value,
      dateTypeFilterRef.current,
      startDateFilterRef.current,
      endDateFilterRef.current
    );
    applyBidTableScroll();
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(bidData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();

    searchBids(newSearchStringTrimmed, applySearch, isAdminPageRef.current);
  };

  const handleCreateBidDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsCreateBidDialogOpen(false);
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.BID_ADMINISTRATION];
  const breadcrumbsLinks = [HOME_PATH];
  const renderDesktopBidAdminPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={false}
        />
        <BidAdminFilterForm
          title={"Bid Administration"}
          onDateTypeFilterChange={handleDateTypeFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          startDateFilter={getStartDateFilterSafely()}
          onStartDateFilterChange={handleStartDateFilterChange}
          endDateFilter={getEndDateFilterSafely()}
          onEndDateFilterChange={handleEndDateFilterChange}
          onBidTypeFilterChange={handleBidTypeFilterChange}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          isDesktopView={props.isDesktopView}
          featureConfig={props.featureConfig}
          workgroupFilters={workgroupFilters}
          allWorkgroupFilters={Object.keys(workgroupData)}
          onWorkgroupFilterChange={handleWorkgroupFilterChange}
          isWorkgroupSelectAllChecked={isWorkgroupSelectAllChecked}
          isWorkgroupSelectAllIndeterminate={isWorkgroupSelectAllIndeterminate}
          stationFilter={getAdminStation(props.featureConfig.userStations, userSession, stationFilter)}
          onStationFilterChange={handleStationFilterChange}
          dateTypeFilter={bidData.dateTypeFilter}
          startDateFilterErrors={bidData.startDateFilterErrors}
          endDateFilterErrors={bidData.endDateFilterErrors}
          bidTypeFilters={bidData.bidTypeFilters}
          allBidTypesFilters={Object.values(bidTypes)}
          bidStatusFilters={bidData.statusFilters}
          searchString={bidData.searchString}
          searchStringErrors={bidData.searchStringErrors}
          setIsCreateBidDialogOpen={setIsCreateBidDialogOpen}
        />

        {isCreateBidDialogOpen && (
          <CreateBidDialog
            isDialogOpen={isCreateBidDialogOpen}
            onClickDialogClose={handleCreateBidDialogClose}
            station={stationFilter}
            bidTypes={bidTypes}
            onSubmitBidEvent={(referenceNumber) => {
              const bidChildItems = props.featureConfig.childItems;
              const manageShiftBidFeature = bidChildItems.find((item) => item.key === FEATURES.MANAGE_BID);
              history.push({ pathname: manageShiftBidFeature.path.replace(":referenceNumber", referenceNumber) });
            }}
          />
        )}

        <div>
          <BidInfoBanner
            bids={filteredStationBids}
            handleInfoFilter={handleBidInfoFilterChange}
            bidInfoFilter={bidInfoFilter}
            isAdmin
          />
          <BidTable
            bidList={isSearch ? searchedStationBids : bidInfoFilter ? bidInfoFilteredStationBids : filteredStationBids}
            featureConfig={props.featureConfig}
            station={stationFilterRef.current}
            isFirstLoad={isFirstLoad}
            isLoading={isLoading}
            isError={isError}
            errorObject={errorObject}
            tableDesktopRef={bidTableDesktopRef}
            setSelectedBidForAuditHistory={setSelectedBidForAuditHistory}
          />
          <BidHistoryDialog
            selectedBidForAuditHistory={selectedBidForAuditHistory}
            setSelectedBidForAuditHistory={setSelectedBidForAuditHistory}
          />
        </div>
      </>
    );
  };

  const renderMobileBidAdminPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents" data-testid="admin-bid-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopBidAdminPage() : renderMobileBidAdminPage()}</>
      )}
    </div>
  );
};

BidAdministrationPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default BidAdministrationPage;
